import { type ElementType, useCallback, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { type DateRange } from 'react-day-picker';
import { PageTitle } from '@/components/page-title';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Clock, CalendarDays, UserPlus, UserX } from 'lucide-react';

import { useGetPostHogMetrics } from '@/hooks/queries/composer-dashboard/use-get-posthog-metrics';
import { useGetCollaborationMetrics } from '@/hooks/queries/composer-dashboard/use-get-collaboration-metrics';
import { useGetAccountMetrics } from '@/hooks/queries/composer-dashboard/use-get-account-metrics';
import { useGetWorkflowInfoMetrics } from '@/hooks/queries/composer-dashboard/use-get-workflow-info-metrics';
import { useGetExecutionDetailMetrics } from '@/hooks/queries/composer-dashboard/use-get-execution-detail-metrics';
import { useGetOutputDeliverableMetrics } from '@/hooks/queries/composer-dashboard/use-get-output-deliverable-metrics';
import { useGetTokenConsumption } from '@/hooks/queries/composer-dashboard/use-get-token-consumption';
import { useGetModelDistributionMetrics } from '@/hooks/queries/composer-dashboard/use-get-model-distribution-metrics';
import { useGetTopWorkflows } from '@/hooks/queries/composer-dashboard/use-get-top-workflows';
import { useGetOrgLabels } from '@/hooks/queries/use-get-org-labels';
import { useGetWorkflowsByLabel } from '@/hooks/queries/composer-dashboard/use-get-workflows-by-label';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';

import { SectionHeading } from './components/section-heading';
import { WorkflowInfoSection } from './components/workflow-info-section';
import { WorkflowLabelsPreviewSection } from './components/workflow-labels-preview-section';
import { ExecutionDetailSection } from './components/execution-detail-section';
import { OutputDeliverableSection } from './components/output-deliverable-section';
import { TokenConsumptionSectionPreview } from './components/token-consumption-section-preview';
import { ModelDistributionSection } from './components/model-distribution-section';
import { TopWorkflowsSection } from './components/top-workflows-section';
import { LabelFilterBand } from './components/label-filter-band';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function KpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: ElementType;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <Skeleton className='h-16 w-full' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
        <CardTitle className='text-sm font-medium'>{title}</CardTitle>
        <Icon className='size-4 text-muted-foreground' />
      </CardHeader>
      <CardContent>
        <div className='text-2xl font-bold'>{value}</div>
        {subtitle && (
          <p className='text-xs text-muted-foreground'>{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function ComposerDashboardPage() {
  const {
    isDashboardUserInfoEnabled,
    isDashboardModelDistributionEnabled,
    isDashboardTokenSpendEnabled,
    isLabelsEnabled,
  } = useLaunchDarkly();
  const [tokenDateRange, setTokenDateRange] = useState<DateRange | undefined>();

  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const toggleLabel = useCallback((id: string) => {
    setSelectedLabelIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);
  const clearLabels = useCallback(() => setSelectedLabelIds([]), []);

  const { data: orgLabelsData, isLoading: isOrgLabelsLoading } =
    useGetOrgLabels({ enabled: isLabelsEnabled });

  const { data: workflowsByLabelData, isLoading: isWorkflowsByLabelLoading } =
    useGetWorkflowsByLabel({
      enabled: isLabelsEnabled,
    });

  const workflowCountsByLabelId = useMemo<Record<string, number> | undefined>(
    () =>
      workflowsByLabelData
        ? Object.fromEntries(
            workflowsByLabelData.labels.map((l) => [
              l.label_id,
              l.workflow_count,
            ]),
          )
        : undefined,
    [workflowsByLabelData],
  );
  const orgLabels = orgLabelsData ?? [];

  const tokenStartDate = tokenDateRange?.from
    ? format(tokenDateRange.from, 'yyyy-MM-dd')
    : undefined;
  const tokenEndDate = tokenDateRange?.to
    ? format(tokenDateRange.to, 'yyyy-MM-dd')
    : undefined;

  const {
    data: postHogData,
    isLoading: isPostHogLoading,
    error: postHogError,
  } = useGetPostHogMetrics(7, { enabled: isDashboardUserInfoEnabled });

  const { data: collaborationData, isLoading: isCollaborationLoading } =
    useGetCollaborationMetrics();

  const { data: accountMetrics, isLoading: isAccountMetricsLoading } =
    useGetAccountMetrics({ enabled: isDashboardUserInfoEnabled });

  // Only pass label IDs that are still present in orgLabels to the metrics
  // hooks. Stale IDs linger in state after a label is deleted and would
  // silently filter results while the filter band shows no active chips.
  const validSelectedLabelIds = useMemo(() => {
    const orgLabelIdSet = new Set(orgLabels.map((l) => l.id));
    return selectedLabelIds.filter((id) => orgLabelIdSet.has(id));
  }, [selectedLabelIds, orgLabels]);

  const labelFilter =
    isLabelsEnabled && validSelectedLabelIds.length > 0
      ? validSelectedLabelIds
      : undefined;

  const {
    data: workflowInfo,
    isLoading: isWorkflowInfoLoading,
    error: workflowInfoError,
  } = useGetWorkflowInfoMetrics(labelFilter);

  const {
    data: executionDetail,
    isLoading: isExecutionDetailLoading,
    error: executionDetailError,
  } = useGetExecutionDetailMetrics(labelFilter);

  const {
    data: outputDeliverable,
    isLoading: isOutputDeliverableLoading,
    error: outputDeliverableError,
  } = useGetOutputDeliverableMetrics(labelFilter);

  const {
    data: tokenConsumption,
    isLoading: isTokenConsumptionLoading,
    error: tokenConsumptionError,
  } = useGetTokenConsumption(tokenStartDate, tokenEndDate, labelFilter, {
    enabled: isDashboardTokenSpendEnabled,
  });

  const {
    data: modelDistribution,
    isLoading: isModelDistributionLoading,
    error: modelDistributionError,
  } = useGetModelDistributionMetrics({
    enabled: isDashboardModelDistributionEnabled,
    labelIds: labelFilter,
  });

  const {
    data: topWorkflows,
    isLoading: isTopWorkflowsLoading,
    error: topWorkflowsError,
  } = useGetTopWorkflows(labelFilter);

  const isUserInfoLoading =
    isPostHogLoading || isCollaborationLoading || isAccountMetricsLoading;

  return (
    <div className='w-full space-y-6'>
      <div className='space-y-5'>
        <div className='space-y-2'>
          <PageTitle title='Dashboard' />
          <p className='text-base text-muted-foreground'>
            Track the performance of your workflows to identify areas for
            optimization
          </p>
        </div>
        <Separator />
      </div>

      <div className='space-y-6'>
        {/* ── Section 1: User Information (behind LD flag) ── */}
        {isDashboardUserInfoEnabled && (
          <div className='space-y-4'>
            <SectionHeading>User Information</SectionHeading>
            <div className='grid grid-cols-2 gap-4 lg:grid-cols-5'>
              <KpiCard
                title='Weekly Active Users'
                value={postHogData?.unique_visitors ?? '—'}
                subtitle='Last 7 days'
                icon={Users}
                isLoading={isUserInfoLoading}
              />
              <KpiCard
                title='Daily Users'
                value={
                  postHogData?.timeseries?.unique_visitors?.length
                    ? (postHogData.timeseries.unique_visitors[
                        postHogData.timeseries.unique_visitors.length - 1
                      ] ?? '—')
                    : '—'
                }
                subtitle='Most recent day'
                icon={CalendarDays}
                isLoading={isUserInfoLoading}
              />
              <KpiCard
                title='Avg Session Duration'
                value={
                  postHogData?.avg_session_duration_seconds
                    ? formatDuration(postHogData.avg_session_duration_seconds)
                    : '—'
                }
                icon={Clock}
                isLoading={isUserInfoLoading}
              />
              <KpiCard
                title='Collaboration Rate'
                value={
                  collaborationData?.collaboration_percent != null
                    ? `${collaborationData.collaboration_percent.toFixed(0)}%`
                    : '—'
                }
                subtitle='Workflows with collaborators'
                icon={UserPlus}
                isLoading={isUserInfoLoading}
              />
              <KpiCard
                title='Never Logged In'
                value={accountMetrics?.accounts_never_logged_in ?? '—'}
                subtitle={
                  accountMetrics
                    ? `of ${accountMetrics.accounts_invited} invited`
                    : undefined
                }
                icon={UserX}
                isLoading={isUserInfoLoading}
              />
            </div>
            {postHogError && !isPostHogLoading && (
              <p className='text-xs text-muted-foreground'>
                Analytics data unavailable — configure URL patterns in Admin
                &gt; Analytics Config
              </p>
            )}
          </div>
        )}

        {/* ── Section 2: Workflow Information ── */}
        <WorkflowInfoSection
          workflowInfo={workflowInfo}
          collaboration={collaborationData}
          isWorkflowInfoLoading={isWorkflowInfoLoading}
          isCollaborationLoading={isCollaborationLoading}
          error={workflowInfoError}
        />

        {isLabelsEnabled && (
          <WorkflowLabelsPreviewSection
            labels={orgLabels}
            isLoading={isOrgLabelsLoading || isWorkflowsByLabelLoading}
            selectedLabelIds={validSelectedLabelIds}
            onToggleLabel={toggleLabel}
            workflowCounts={workflowCountsByLabelId}
          />
        )}

        {/* ── Section 3 + 5: Workflow Execution & Output side by side ── */}
        <div className='grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2'>
          <ExecutionDetailSection
            metrics={executionDetail}
            isLoading={isExecutionDetailLoading}
            error={executionDetailError}
          />
          <OutputDeliverableSection
            metrics={outputDeliverable}
            isLoading={isOutputDeliverableLoading}
            error={outputDeliverableError}
          />
        </div>

        {/* ── Section 6: Token Spend (behind LD flag) ── */}
        {isDashboardTokenSpendEnabled && (
          <TokenConsumptionSectionPreview
            metrics={tokenConsumption}
            isLoading={isTokenConsumptionLoading}
            error={tokenConsumptionError}
            dateRange={tokenDateRange}
            onDateRangeChange={setTokenDateRange}
            selectedLabelIds={validSelectedLabelIds}
            onToggleLabel={isLabelsEnabled ? toggleLabel : undefined}
          />
        )}

        {/* ── Section 7: Model Distribution (behind LD flag) ── */}
        {isDashboardModelDistributionEnabled && (
          <ModelDistributionSection
            metrics={modelDistribution}
            isLoading={isModelDistributionLoading}
            error={modelDistributionError}
          />
        )}

        {/* ── Extra: Top Executed Workflows ── */}
        <TopWorkflowsSection
          data={topWorkflows}
          isLoading={isTopWorkflowsLoading}
          error={topWorkflowsError}
        />
      </div>

      {isLabelsEnabled && (
        <LabelFilterBand
          allLabels={orgLabels}
          selectedLabelIds={selectedLabelIds}
          onToggleLabel={toggleLabel}
          onClear={clearLabels}
          isLoading={isOrgLabelsLoading}
        />
      )}
    </div>
  );
}
